import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';

const ThemeToggle = ({ className = '', variant = 'slider' }) => {
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';

    if (variant === 'icon') {
        // Icon-only style for mobile header
        return (
            <button
                onClick={toggleTheme}
                className={`p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${className}`}
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            >
                {isDark ? (
                    <SunIcon className="h-6 w-6" />
                ) : (
                    <MoonIcon className="h-6 w-6" />
                )}
            </button>
        );
    }

    if (variant === 'button') {
        // Modern button style with text
        return (
            <button
                onClick={toggleTheme}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isDark 
                        ? 'bg-white hover:bg-gray-100 text-gray-900' 
                        : 'bg-gray-700 hover:bg-gray-800 text-gray-100'
                } ${className}`}
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            >
                {isDark ? (
                    <>
                        <SunIcon className="w-4 h-4" />
                        <span>Enable Light Mode</span>
                    </>
                ) : (
                    <>
                        <MoonIcon className="w-4 h-4" />
                        <span>Enable Dark Mode</span>
                    </>
                )}
            </button>
        );
    }

    // Default slider style
    return (
        <div className={`flex items-center gap-3 ${className}`}>
            {/* Theme label with icon */}
            <div className="flex items-center gap-2">
                {isDark ? (
                    <MoonIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                ) : (
                    <SunIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                )}
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {isDark ? 'Dark Mode' : 'Light Mode'}
                </span>
            </div>

            {/* Modern slider toggle */}
            <button
                onClick={toggleTheme}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isDark 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-300 hover:bg-gray-400'
                }`}
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            >
                {/* Slider button */}
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                        isDark ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
            </button>
        </div>
    );
};

// Advanced theme selector with dropdown
export const ThemeSelector = ({ className = '' }) => {
    const { theme, setLightTheme, setDarkTheme, setSystemTheme } = useTheme();
    const [isOpen, setIsOpen] = React.useState(false);

    const themes = [
        {
            id: 'light',
            name: 'Light',
            icon: SunIcon,
            action: setLightTheme
        },
        {
            id: 'dark',
            name: 'Dark',
            icon: MoonIcon,
            action: setDarkTheme
        },
        {
            id: 'system',
            name: 'System',
            icon: ComputerDesktopIcon,
            action: setSystemTheme
        }
    ];

    const currentTheme = themes.find(t => t.id === theme) || themes[0];

    return (
        <div className={`relative ${className}`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors duration-200"
            >
                <currentTheme.icon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {currentTheme.name}
                </span>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    {themes.map((themeOption) => (
                        <button
                            key={themeOption.id}
                            onClick={() => {
                                themeOption.action();
                                setIsOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg ${
                                theme === themeOption.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'
                            }`}
                        >
                            <themeOption.icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{themeOption.name}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Backdrop to close dropdown */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default ThemeToggle;